import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface GeneContent {
  isActive: boolean;
  geneInfo: {
    symbol: string;
    id: number | null;
    name: string;
  };
}

export interface MappingContent {
  isActive: boolean;
  name?: string;
  tabSegment?: string;
  viewSegment?: string;
  mappingItems?: unknown[];
  columVisibilityModelKey?: string;
  selectedPmYear?: number;
  selectedPmYearFrom?: number;
  selectedPmYearTo?: number;
  selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL';
  selectedPolarity?: 'Positive' | 'Negative';
}

export type SelectedInfoItem = { id: string | number; [key: string]: unknown };

export interface NavigationState {
  // Core navigation state
  view: string;
  source: string;
  tab: string;
  searchItems: any[];
  hasValidParams: boolean;

  // Info sidebar state
  selectedInfoItem: SelectedInfoItem | null;
  geneContent: GeneContent;
  mappingContent: MappingContent;

  // Actions for core navigation
  setView: (view: string) => void;
  setSource: (source: string) => void;
  setTab: (tab: string) => void;
  setSearchItems: (items: any[]) => void;
  setHasValidParams: (hasValid: boolean) => void;

  // Actions for info sidebar
  setSelectedInfoItem: (item: SelectedInfoItem | null) => void;
  setGeneContent: (content: GeneContent) => void;
  setMappingContent: (content: MappingContent) => void;

  // Utility actions
  resetNavigation: () => void;
  updateSearchState: (view: string, source: string, tab: string, items: any[], hasValid: boolean) => void;
  clearSidebarContent: () => void;
}

// Default state values
const getDefaultNavigationState = () => ({
  view: '',
  source: '',
  tab: '',
  searchItems: [],
  hasValidParams: false,
  selectedInfoItem: null,
  geneContent: {
    isActive: false,
    geneInfo: { symbol: '', id: null, name: '' },
  },
  mappingContent: {
    isActive: false,
  },
});

export const useNavigationStore = create<NavigationState>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...getDefaultNavigationState(),

      // Core navigation actions
      setView: view => set({ view }),
      setSource: source => set({ source }),
      setTab: tab => set({ tab }),
      setSearchItems: searchItems => set({ searchItems }),
      setHasValidParams: hasValidParams => set({ hasValidParams }),

      // Info sidebar actions
      setSelectedInfoItem: selectedInfoItem => set({ selectedInfoItem }),
      setGeneContent: geneContent => set({ geneContent }),
      setMappingContent: mappingContent => set({ mappingContent }),

      // Utility actions
      resetNavigation: () => set(getDefaultNavigationState()),

      // Batch update for search state
      updateSearchState: (view, source, tab, searchItems, hasValidParams) =>
        set({ view, source, tab, searchItems, hasValidParams }),

      // Clear all sidebar content
      clearSidebarContent: () =>
        set({
          selectedInfoItem: null,
          geneContent: {
            isActive: false,
            geneInfo: { symbol: '', id: null, name: '' },
          },
          mappingContent: {
            isActive: false,
          },
        }),
    }),
    {
      name: 'navigation-store',
    },
  ),
);

// Computed selectors for common navigation checks
export const useNavigationSelectors = () => {
  const navigation = useNavigationStore();

  return {
    // Check if we're in a search context
    isInSearchContext: navigation.view !== '' && navigation.source !== '',

    // Check if search has results
    hasSearchResults: navigation.searchItems.length > 0,

    // Check if sidebar has content
    hasSidebarContent:
      navigation.selectedInfoItem !== null ||
      navigation.geneContent.isActive ||
      navigation.mappingContent.isActive,

    // Get current search context
    getCurrentSearchContext: () => ({
      view: navigation.view,
      source: navigation.source,
      tab: navigation.tab,
      hasResults: navigation.searchItems.length > 0,
      hasValidParams: navigation.hasValidParams,
    }),

    // Get sidebar state
    getSidebarState: () => ({
      selectedInfoItem: navigation.selectedInfoItem,
      geneContent: navigation.geneContent,
      mappingContent: navigation.mappingContent,
      hasContent:
        navigation.selectedInfoItem !== null ||
        navigation.geneContent.isActive ||
        navigation.mappingContent.isActive,
    }),

    // Check specific navigation states
    isViewActive: (viewName: string) => navigation.view === viewName,
    isSourceActive: (sourceName: string) => navigation.source === sourceName,
    isTabActive: (tabName: string) => navigation.tab === tabName,

    // Get navigation breadcrumb
    getNavigationBreadcrumb: () => {
      const breadcrumb = [];
      if (navigation.view) breadcrumb.push(navigation.view);
      if (navigation.source) breadcrumb.push(navigation.source);
      if (navigation.tab) breadcrumb.push(navigation.tab);
      return breadcrumb;
    },
  };
};
